import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { GIButton, Modal } from 'app-components';
import { ModalContextType } from '../../../../contexts/modalContext';
import { MaskedInput, DATE_MASK, ValidationErrorMessage, useAppSelector, environmentSelector } from '../../utils';
import useApiHandling from '../../hooks/useApiHandling';
import { isDateValid } from '../../../../utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '../../../../utils/dates/dateFormats.utils';

const StyledField = styled.div`
    .date-picker,
    .shown.error--msg {
        margin-top: 4px !important;
        padding: 8px;
        background-color: ${({ theme }) => theme.dangerLightest};
    }
    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
    }

    .gi-input {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
    }

    .gi-input__label {
        margin-left: -10px;
    }

    @media (max-width: 768px) {
        .gi-input__label {
            margin-left: 0px;
        }
    }
`;

const StyledModalBody = styled.div`
    .gi-input__input {
        width: 115px;
    }

    @media (max-width: 768px) {
        .gi-modal__body {
            max-height: 0%;
            padding-bottom: 20px;
        }
    }
`;

const StyledControls = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;

    @media (max-width: 768px) {
        margin-top: 0;
        flex-direction: column;
        gap: 5px;
    }
`;

interface IModalProps extends ModalContextType {
    caseNumber: string;
}

const CancelTerminatePlanModal = ({ closeModal, caseNumber }: IModalProps) => {
    const [cancelDate, setCancelDate] = useState('');
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const { terminatePlan } = useApiHandling();
    const { exchangeName } = useAppSelector(environmentSelector);

    const handleCovStartdateChange = (date: string) => {
        setCancelDate(date);
        if (isDateValid(date, MM_SLASH_DD_SLASH_YYYY)) {
            setErrorMsg(null);
        } else {
            setErrorMsg('cap.applications.modal.csrspedateerror');
        }
    };

    function onClose() {
        closeModal('cancelTerminatePlanModal');
    }

    function onContinue() {
        terminatePlan(caseNumber, cancelDate);
    }

    return (
        <Modal id="cancel_terminate_plan_modal">
            <Modal.Header
                id="cancel_terminate_plan_modal_title"
                titleId="cap.applications.modal.cancelorterminatemyplan2"
                onHide={onClose}
            />
            <StyledModalBody>
                <Modal.Body>
                    <p data-testid="cancel_terminate_plan_modal_body_content">
                        <FormattedMessage id="cap.application.modal.csrtermdatecontent" values={{ exchangeName }} />
                    </p>
                    <StyledField data-testid="cancel_terminate_plan_modal_termination_date">
                        <MaskedInput
                            label={{
                                labelText: 'cap.applications.modal.csrentertermdate',
                                labelClasses: [''],
                                wrapperClasses: [''],
                                isRequired: true,
                            }}
                            input={{
                                id: 'cancelTerm_date',
                                name: 'cancelTermPlan',
                                placeholder: 'mm/dd/yyyy',
                                type: 'text',
                                isInputRequired: true,
                                value: cancelDate,
                                mask: DATE_MASK,
                            }}
                            actions={{
                                handleFieldChange: (date: string) => {
                                    setCancelDate(date);
                                },
                                handleFieldBlur: (date: string) => handleCovStartdateChange(date),
                            }}
                        />
                        <ValidationErrorMessage isVisible={errorMsg !== null} messageText={errorMsg} />
                    </StyledField>
                </Modal.Body>
            </StyledModalBody>
            <Modal.Controls>
                <StyledControls>
                    <GIButton
                        id="cancel_terminate_plan_modal_disenroll_button"
                        onClickHandler={onContinue}
                        isDisabled={errorMsg !== null}
                        variant="primary"
                        labelId="cap.applications.modal.disenroll"
                    />
                    <GIButton 
                        id="cancel_terminate_plan_modal_cancel_button" 
                        onClickHandler={onClose} 
                        variant="secondary" 
                        labelId="button.cancel" 
                    />
                </StyledControls>
            </Modal.Controls>
        </Modal>
    );
};

export default CancelTerminatePlanModal;
